import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  InputBase,
  Divider,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings,
  Logout,
  Person,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getPartnerAuth, clearPartnerAuth } from '../../utils/auth';
import { useNavigate } from 'react-router-dom';


const PartnerTopNav = ({ partnerId, isCollapsed }) => {
  const theme = useTheme();

  const navigate = useNavigate();
  const { partner_name } = getPartnerAuth();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    clearPartnerAuth();
    navigate('/login');
    handleMenuClose();
  };

  const handleProfileClick = () => {
    navigate(`/partner/${partnerId}/profile`);
    handleMenuClose();
  };

  const handleSettingsClick = () => {
    navigate(`/partner/${partnerId}/settings`);
    handleMenuClose();
  };

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      title: 'New mother registered',
      message: 'Sarah Johnson has joined your program',
      time: '2 minutes ago',
      type: 'info'
    },
    {
      id: 2,
      title: 'Match funding approved',
      message: 'Your funding request for $500 has been approved',
      time: '1 hour ago',
      type: 'success'
    },
    {
      id: 3,
      title: 'Monthly report ready',
      message: 'Your monthly contribution report is available',
      time: '3 hours ago',
      type: 'warning'
    }
  ];

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success': return '#BBFFC1';
      case 'warning': return '#FF9800';
      case 'error': return '#F44336';
      default: return '#2196F3';
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: `calc(100% - ${isCollapsed ? '80px' : '280px'})`,
        ml: `${isCollapsed ? '80px' : '280px'}`,
        backgroundColor: '#FFFFFF',
        color: '#000000',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ px: 3, py: 1, width: '100%', display: 'flex', justifyContent: 'space-between' }}>
        {/* Search Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#F5F5F5',
            borderRadius: 3,
            px: 2,
            py: 1,
            width: '80%',
            maxWidth: '80%',
          }}
        >
          <SearchIcon sx={{ color: '#757575', mr: 1, fontSize: 20 }} />
          <InputBase
            placeholder="Search mothers, contributions, activities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flexGrow: 1,
              fontSize: '0.9375rem',
              '& .MuiInputBase-input': {
                color: '#000000',
                '&::placeholder': {
                  color: '#757575',
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {/* Notifications */}
          <IconButton
            onClick={handleNotificationMenuOpen}
            sx={{
              color: '#BBFFC1',
              backgroundColor: 'rgba(249, 41, 85, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(249, 41, 85, 0.08)',
              },
            }}
          >
            <Badge badgeContent={notifications.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Profile Menu */}
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              color: '#BBFFC1',
              backgroundColor: 'rgba(249, 41, 85, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(249, 41, 85, 0.08)',
              },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: '#f92955',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {partner_name ? partner_name.charAt(0).toUpperCase() : 'P'}
            </Avatar>
          </IconButton>
        </Box>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 320,
              maxHeight: 400,
              boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
              borderRadius: 2,
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
              Notifications
            </Typography>
          </Box>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  sx={{
                    py: 2,
                    px: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(249, 41, 85, 0.04)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Chip
                        label={notification.type}
                        size="small"
                        sx={{
                          backgroundColor: getNotificationColor(notification.type),
                          color: 'white',
                          fontSize: '0.75rem',
                          height: 20,
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: '#000000' }}
                      >
                        {notification.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#666', mb: 1 }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#999', fontSize: '0.75rem' }}
                    >
                      {notification.time}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  No new notifications
                </Typography>
              </Box>
            )}
          </Box>
        </Menu>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
              borderRadius: 2,
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000000' }}>
              {partner_name || 'Partner'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Partner Account
            </Typography>
          </Box>
          
          <MenuItem onClick={handleProfileClick} sx={{ py: 1.5 }}>
            <Person sx={{ mr: 2, color: '#BBFFC1' }} />
            <Typography>Profile</Typography>
          </MenuItem>
          
          <MenuItem onClick={handleSettingsClick} sx={{ py: 1.5 }}>
            <Settings sx={{ mr: 2, color: '#BBFFC1' }} />
            <Typography>Settings</Typography>
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#F44336' }}>
            <Logout sx={{ mr: 2 }} />
            <Typography>Logout</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default PartnerTopNav;
